import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
} from '@mui/material';
import { useUpdateMiseAccountSettingsNext } from '@/lib/query/hooks/useMiseAccounts';
import type { AccountSettings, AccountSettingsNext } from '@/lib/types/mise/account';

interface AccountSettingsNextEditDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  currentSettings: AccountSettings | null;
  pendingNext: AccountSettingsNext | null;
}

/** 各フィールドの入力状態 */
interface FieldState {
  value: string;
  error: string;
}

type FieldKey =
  | 'inactiveStockAggregationDay'
  | 'sellerThresholdStockDay'
  | 'sellerThresholdWarehouseStockQuantity'
  | 'warehouseThresholdStockDay'
  | 'coPurchaseAggregationDay';

/**
 * バッチ集計条件 5 項目の next（翌日バッチで適用される値）を編集するダイアログ。
 *
 * 5 項目すべてについて、入力欄が空なら next に書き込まない（= current 値を維持）、
 * 値があれば next に UPSERT する。swap は mise batch_cache が翌日バッチ成功後に行う。
 */
export function AccountSettingsNextEditDialog({
  open,
  onClose,
  accountId,
  currentSettings,
  pendingNext,
}: AccountSettingsNextEditDialogProps) {
  const [fields, setFields] = useState<Record<FieldKey, FieldState>>({
    inactiveStockAggregationDay: { value: '', error: '' },
    sellerThresholdStockDay: { value: '', error: '' },
    sellerThresholdWarehouseStockQuantity: { value: '', error: '' },
    warehouseThresholdStockDay: { value: '', error: '' },
    coPurchaseAggregationDay: { value: '', error: '' },
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [formError, setFormError] = useState<string>('');

  const updateMutation = useUpdateMiseAccountSettingsNext(accountId);

  // 初期値: pendingNext があればそれ、無ければ空欄（= 「変更しない」）
  useEffect(() => {
    if (open) {
      setFields({
        inactiveStockAggregationDay: {
          value: pendingNext?.inactive_stock_aggregation_day?.toString() ?? '',
          error: '',
        },
        sellerThresholdStockDay: {
          value: pendingNext?.out_of_stock_seller_threshold_stock_day?.toString() ?? '',
          error: '',
        },
        sellerThresholdWarehouseStockQuantity: {
          value:
            pendingNext?.out_of_stock_seller_threshold_warehouse_stock_quantity?.toString() ?? '',
          error: '',
        },
        warehouseThresholdStockDay: {
          value: pendingNext?.out_of_stock_warehouse_threshold_stock_day?.toString() ?? '',
          error: '',
        },
        coPurchaseAggregationDay: {
          value: pendingNext?.co_purchase_aggregation_day?.toString() ?? '',
          error: '',
        },
      });
      setFormError('');
    }
  }, [open, pendingNext]);

  const handleClose = () => {
    if (!updateMutation.isPending) {
      setConfirmDialogOpen(false);
      onClose();
    }
  };

  /**
   * 入力変更時のバリデーション。
   * - 空は OK（= 「この項目は変更しない」）
   * - 入力があれば整数 + min 以上
   */
  const validateField = (key: FieldKey, value: string): string => {
    if (value === '') return '';
    const num = Number(value);
    if (!Number.isInteger(num)) {
      return '整数を入力してください';
    }
    const min = key === 'sellerThresholdWarehouseStockQuantity' ? 0 : 1;
    if (num < min) {
      return `${min}以上の整数を入力してください`;
    }
    return '';
  };

  const handleFieldChange = (key: FieldKey, value: string) => {
    setFields((prev) => ({
      ...prev,
      [key]: { value, error: validateField(key, value) },
    }));
    setFormError('');
  };

  const handleSave = () => {
    // 全フィールド空 → 変更なし
    const allEmpty = (Object.keys(fields) as FieldKey[]).every((k) => fields[k].value === '');
    if (allEmpty) {
      setFormError('変更する項目を 1 つ以上入力してください');
      return;
    }
    // エラーがある場合は保存不可
    const hasError = (Object.keys(fields) as FieldKey[]).some((k) => fields[k].error !== '');
    if (hasError) return;

    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);

    const toNum = (v: string): number | null => (v === '' ? null : Number(v));

    const request = {
      inactive_stock_aggregation_day: toNum(fields.inactiveStockAggregationDay.value),
      out_of_stock_seller_threshold_stock_day: toNum(fields.sellerThresholdStockDay.value),
      out_of_stock_seller_threshold_warehouse_stock_quantity: toNum(
        fields.sellerThresholdWarehouseStockQuantity.value,
      ),
      out_of_stock_warehouse_threshold_stock_day: toNum(fields.warehouseThresholdStockDay.value),
      co_purchase_aggregation_day: toNum(fields.coPurchaseAggregationDay.value),
    };

    try {
      await updateMutation.mutateAsync(request);
      onClose();
    } catch (error) {
      console.error('Failed to update account settings next:', error);
    }
  };

  const renderField = (key: FieldKey, label: string, currentValue: number, min: number) => (
    <TextField
      fullWidth
      label={label}
      type="number"
      value={fields[key].value}
      onChange={(e) => handleFieldChange(key, e.target.value)}
      error={!!fields[key].error}
      helperText={
        fields[key].error ||
        `現在の値: ${currentValue}（空欄にすると変更しません。翌日のバッチで反映されます）`
      }
      inputProps={{ min, step: 1 }}
      sx={{ mb: 2 }}
    />
  );

  const hasAnyError = (Object.keys(fields) as FieldKey[]).some((k) => fields[k].error !== '');
  const hasAnyValue = (Object.keys(fields) as FieldKey[]).some((k) => fields[k].value !== '');

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>集計前の設定を編集（バッチ集計条件）</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              ここで入力した値は翌日のバッチ実行時に反映されます。空欄にした項目は変更されません。
              {pendingNext && (
                <>
                  <br />
                  現在、反映待ちの予約があります。保存すると上書きされます。
                </>
              )}
            </Alert>

            {currentSettings ? (
              <>
                {renderField(
                  'inactiveStockAggregationDay',
                  '非稼働リストの集計日数',
                  currentSettings.inactive_stock_aggregation_day,
                  1,
                )}
                {renderField(
                  'sellerThresholdStockDay',
                  '店舗欠品リスクとみなす在庫日数しきい値',
                  currentSettings.out_of_stock_seller_threshold_stock_day,
                  1,
                )}
                {renderField(
                  'sellerThresholdWarehouseStockQuantity',
                  '店舗欠品リスクとみなす倉庫在庫数しきい値',
                  currentSettings.out_of_stock_seller_threshold_warehouse_stock_quantity,
                  0,
                )}
                {renderField(
                  'warehouseThresholdStockDay',
                  '倉庫欠品リスクとみなす在庫日数しきい値',
                  currentSettings.out_of_stock_warehouse_threshold_stock_day,
                  1,
                )}
                {renderField(
                  'coPurchaseAggregationDay',
                  '併売の集計日数',
                  currentSettings.co_purchase_aggregation_day,
                  1,
                )}
              </>
            ) : (
              <Alert severity="warning">
                アカウント設定が未登録のため、編集できません。先にアカウント設定を作成してください。
              </Alert>
            )}

            {formError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}

            {updateMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : '集計前の設定の更新に失敗しました'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateMutation.isPending || !currentSettings || hasAnyError || !hasAnyValue}
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400,
          },
        }}
      >
        <DialogTitle>集計前の設定を更新しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            入力した値は翌日のバッチ実行時に反映されます。空欄の項目は変更されません。よろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
